/*
 * Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 * Author Rihard Tsybulka <rykhard.tsybulka@noogadev.com>
 */

import { WithTranslation } from 'react-i18next';
import {
    AiCategory,
    DocType,
    PaymentTypesAsStrings,
    ProductItemInfo,
    QuantityTypes,
    VatAmounts,
} from '../../../data/api/documents/documentsClient.typings';
import { DATA_UNAVAILABLE } from '../../../utils/utils';
import { AiCategoriesType } from '../../../data/api/apiCommon.typings';

export const DOC_FULL_NAME_BY_DOC_TYPE: { [key in DocType]: string } = {
    [DocType.Sale]: 'document.typeFullName.saleDocument',
    [DocType.Deposit]: 'document.typeFullName.depositDocument',
    [DocType.Withdraw]: 'document.typeFullName.withdrawDocument',
    [DocType.MoneyBack]: 'document.typeFullName.moneybackDocument',
    [DocType.RollBack]: 'document.typeFullName.rollbackDocument',
    [DocType.Correction]: 'document.typeFullName.correctionDocument',
    [DocType.Prepayment]: 'document.typeFullName.prepayDocument',
    [DocType.CreditPay]: 'document.typeFullName.creditpayDocument',
    [DocType.OpenShift]: 'document.typeFullName.openShiftDocument',
    [DocType.ZReport]: 'document.typeFullName.zReportDocument',
};

export const PAYMENT_ICON_BY_PAYMENT_TYPE: { [key in PaymentTypesAsStrings]: string } = {
    [PaymentTypesAsStrings.CASH]: 'money',
    [PaymentTypesAsStrings.CASHLESS]: 'credit_card',
    [PaymentTypesAsStrings.CREDIT]: 'account_balance',
    [PaymentTypesAsStrings.BONUS]: 'card_giftcard',
    [PaymentTypesAsStrings.PREPAYMENT]: 'account_balance_wallet',
};

export const makeDocumentTitle = (docType: DocType, docNumber: number, t: WithTranslation['t']): string => {
    if (docType === null || docType === undefined || docNumber === null || docNumber === undefined)
        return DATA_UNAVAILABLE;

    return `${t(DOC_FULL_NAME_BY_DOC_TYPE[docType])}  № ${docNumber}`;
};

export const defineTaxType = (vatPercent: string, t: WithTranslation['t']): string => {
    return vatPercent === '2' || vatPercent === '8'
        ? t('domain.taxationTypes.SIMPLIFIED')
        : t('domain.taxationTypes.VAT');
};

export const defineProductItemTaxRate = (item: ProductItemInfo, t: WithTranslation['t']): string => {
    if (!item.hasOwnProperty('itemVatPercent') || item.itemVatPercent === 0 || item.itemVatPercent === null) {
        return `${t('document.notTaxable')}`;
    }
    if (
        (item.hasOwnProperty('itemMarginPrice') && item.itemMarginPrice !== 0 && item.itemMarginPrice !== null) ||
        (item.hasOwnProperty('itemMarginSum') && item.itemMarginSum !== 0 && item.itemMarginSum !== null)
    ) {
        return `${t('document.KTM')}: ${item.itemVatPercent}%`;
    }
    if (item.itemVatPercent === 2 || item.itemVatPercent === 8) {
        return `${t('domain.taxationTypes.SIMPLIFIED')}: ${item.itemVatPercent}%`;
    }
    return `${t('domain.taxationTypes.VAT')}: ${item.itemVatPercent}%`;
};

export const formatProductItemTax = (item: ProductItemInfo, t: WithTranslation['t']): string => {
    return `${defineProductItemTaxRate(item, t)} ${
        item.hasOwnProperty('itemVatAmount') && !!item.itemVatAmount
            ? `= ${checkAmountOfMoney(item.itemVatAmount)}`
            : ''
    }`;
};

export const groupTaxesByVat = (vatAmounts: VatAmounts[]): { [name: string]: VatAmounts } => {
    if (!vatAmounts) return {};
    const taxesRates: { [key: string]: VatAmounts } = {};
    vatAmounts.forEach((item) => {
        const { vatPercent, vatSum, vatResult } = item;
        if (item.hasOwnProperty('vatPercent') && vatPercent !== 0 && vatPercent !== null) {
            if (taxesRates[vatPercent]) {
                taxesRates[vatPercent].vatSum += vatSum;
                taxesRates[vatPercent].vatResult += vatResult;
            } else {
                taxesRates[vatPercent] = {
                    vatSum,
                    vatResult,
                };
            }
        } else {
            taxesRates['notTaxable']
                ? (taxesRates['notTaxable'].vatSum += vatSum)
                : (taxesRates['notTaxable'] = { vatSum, vatResult });
        }
    });
    return taxesRates;
};

export const UNITS_BY_QUANTITY_TYPE: Record<QuantityTypes, string> = {
    [QuantityTypes.Things]: 'document.units.things',
    [QuantityTypes.Kilos]: 'document.units.kilos',
    [QuantityTypes.Liters]: 'document.units.liters',
    [QuantityTypes.Meters]: 'document.units.meters',
    [QuantityTypes.SquareMeters]: 'document.units.squareMeters',
    [QuantityTypes.CubicMeters]: 'document.units.cubicMeters',
};

export const checkItemQuantityType = (itemQuantityType: QuantityTypes): boolean => {
    if (itemQuantityType === null || itemQuantityType === undefined) {
        return false;
    }
    return Object.keys(UNITS_BY_QUANTITY_TYPE).includes(itemQuantityType.toString());
};

export const formatQuantityType = (itemQuantityType: QuantityTypes, t: WithTranslation['t']): string => {
    if (!checkItemQuantityType(itemQuantityType)) return '-';
    return t(`${UNITS_BY_QUANTITY_TYPE[itemQuantityType]}`);
};

export const formatQuantityValue = (product: ProductItemInfo): string => {
    const value = Number(product.itemQuantity);
    if (product.itemQuantity === null || isNaN(value)) return '-';
    if (product.itemQuantityType === QuantityTypes.Things) {
        if (Number.isInteger(value)) return value.toString();
        else return value.toFixed(2);
    }
    return value.toFixed(3);
};

export const formatAiCategory = (categoryItem: AiCategory, t: WithTranslation['t']): string => {
    if (categoryItem) {
        return `${
            AiCategoriesType[categoryItem.category]
                ? t(`domain.aiCategoriesList.${categoryItem.category}`)
                : categoryItem.category
        } ${
            categoryItem.similarity
                ? `(${t('domain.aiProbability').toLocaleLowerCase()} ${categoryItem.similarity + '%'})`
                : ''
        } `;
    } else {
        return DATA_UNAVAILABLE;
    }
};

export const checkAmountOfMoney = (amount: unknown): string => {
    if (typeof Number(amount) !== 'number' || amount === null || amount === undefined) {
        return '0.00';
    }
    return Number(amount).toFixed(2);
};

export const formatAmountOfMoney = (amount: number, isStrict: boolean): string => {
    if (typeof Number(amount) !== 'number' || amount === null || amount === undefined) {
        return isStrict ? DATA_UNAVAILABLE : '-';
    }
    return Number(amount).toFixed(2);
};

export const arrayBufferToBase64 = (arrayBuffer: ArrayBuffer) => {
    return btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
};

export const formatDocumentFormatVersion = (version: number): string => {
    if (!version) return '-';
    return version.toString();
};

export const getNumberOfPunchedPerShiftDocuments = (firstDocNumber: number, lastDocNumber: number): string => {
    if (!firstDocNumber && !lastDocNumber) return '0';
    if (!firstDocNumber || !lastDocNumber || lastDocNumber < firstDocNumber) return DATA_UNAVAILABLE;

    return (lastDocNumber - firstDocNumber + 1).toString();
};

export const formatDocumentSum = (docType: DocType, sum: number): string => {
    if (docType === DocType.OpenShift) return '-';
    if (docType === DocType.ZReport) return formatAmountOfMoney(sum, false);
    return formatAmountOfMoney(sum, true);
};

export const trancateTextOverflow = (value: string, maxLength: number): string => {
    if (value?.length > maxLength) return value.slice(0, maxLength) + '...';
    else return value;
};
